import { Box, Button, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { EnquiryDetail } from '../../services/sellOldAC';
import { useEffect, useMemo, useState } from 'react';
import { ISellOldACDetail } from '../../utils/entities/interface';
import { Add, Cancel, Engineering, RemoveRedEyeOutlined } from '@mui/icons-material';
import AssignTechnicianModal from '../Booking/actions/TechnicianModal';
import { DetailPageHeader } from '../../components/layout/DetailPageHeader';
import { GETQuotesForEnquiry, UpdateEnquiryStatus } from '../../services/requests';
import useSnackbar from '../../hooks/useSnackbar';
import { capitalizeWords } from '../../utils/helper';
import AddQuotation from '../Amc/AddQuotation';
import { QuotationsCard } from '../Amc/AMCRequest';

const composeAddress = (address?: ISellOldACDetail['addressDetails']) => {
  if (!address) return 'N/A';
  const parts = [
    address.house,
    address.street,
    address.landmark,
    address.city,
    address.state,
    address.zipcode,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'N/A';
};

function SellRequestDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const enquiryId = location?.state as string;
  const [detailValue, setDetailValue] = useState<ISellOldACDetail | null>(null);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [assignModal, setAssignModal] = useState<boolean>(false);
  const [quoteModal, setQuoteModal] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  useEffect(() => {
    fetchDetail();
  }, []);

  async function fetchDetail() {
    await EnquiryDetail(enquiryId).then((response) => {
      setDetailValue(response.data);
    });
  }

  useEffect(() => {
    if (!detailValue?._id) {
      setQuotes([]);
      return;
    }
    GETQuotesForEnquiry(detailValue._id).then(setQuotes);
  }, [detailValue?._id]);

  const acDetails = useMemo(() => {
    const rows = detailValue?.oldAcDetails;
    return Array.isArray(rows) ? rows : [];
  }, [detailValue]);

  const handleCancel = async () => {
    if (!detailValue?.enquiryId) return;
    try {
      setCancelling(true);
      await UpdateEnquiryStatus(detailValue.enquiryId, 'CANCELLED', cancelReason.trim());
      showSnackbar('Sell old AC request cancelled', 'success');
      setCancelDialog(false);
      setCancelReason('');
      fetchDetail();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to cancel request', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const isCancelled = detailValue?.status === 'CANCELLED';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100%' }}>
      <DetailPageHeader title="Sell Old AC Details" />
      {SnackbarComponent}

      <div className="px-5 mt-5">
        <div className="w-full mx-auto bg-[#1a1a1a] rounded-xl shadow-lg border border-[#2a2a2a]">
          <div className="flex justify-between items-center px-6 py-4 border-b border-[#2a2a2a]">
            <div className="flex items-center gap-2 text-lg font-medium text-white">
              Customer Details
            </div>

            <div className="text-sm text-white">
              Status :
              <span className={`font-medium ml-1 ${isCancelled ? 'text-red-500' : 'text-green-500'}`}>
                {detailValue?.status || 'N/A'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 px-6 py-6 text-sm bg-[#232323]">
            <div className="space-y-4">
              <DetailRow label="Full Name" value={detailValue?.user_info?.name || 'N/A'} green />
              <DetailRow label="Customer Type" value={capitalizeWords(String(detailValue?.user_info?.type || 'N/A')).replaceAll('_', ' ')} />
              <DetailRow label="Place Type" value={capitalizeWords(String(detailValue?.propertyType || detailValue?.details?.propertyType || 'N/A')).replaceAll('_', ' ')} />
              <DetailRow label="Address" value={composeAddress(detailValue?.addressDetails)} />
              <DetailRow label="Total AC" value={String(detailValue?.noOfAc || acDetails.length || detailValue?.totalNoOfAC || 'N/A')} />
            </div>

            <div className="space-y-4">
              <DetailRow label="Contact Number" value={detailValue?.user_info?.phoneNumber || 'N/A'} />
              <DetailRow label="Inspection Date" value={detailValue?.schedule?.date ? new Date(detailValue.schedule.date).toLocaleDateString() : 'N/A'} />
              <DetailRow label="Inspection Time" value={detailValue?.schedule?.slot?.replace("_", " ") || 'N/A'} />
              {isCancelled && (
                <DetailRow label="Cancellation Reason" value={detailValue?.cancelReason || 'N/A'} />
              )}

              <div className="flex items-center justify-between">
                <p className="text-gray-400 w-full">Assign Inspector</p>
                {detailValue?.technician_info ? (
                  <p className="w-full text-white flex items-center justify-between">
                    {detailValue.technician_info.name}{' '}
                    <RemoveRedEyeOutlined
                      onClick={() => navigate(`/technicians/${detailValue.technician_info?._id}`)}
                      style={{ color: 'red', cursor: 'pointer' }}
                    />
                  </p>
                ) : (
                  <p className="w-full text-white flex items-center justify-between">-</p>
                )}
              </div>
            </div>
          </div>

          {acDetails.length === 0 ? (
            <Box sx={{ color: '#fff' }}>
              <CardHeader title="AC Details" titleTypographyProps={{ fontWeight: 600, fontSize: 18 }} />
              <Divider sx={{ borderColor: '#565656' }} />
              <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
                <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                  No AC details provided
                </Typography>
              </CardContent>
            </Box>
          ) : (
            acDetails.map((ac, index) => (
              <div key={ac._id || index}>
                <div className="border-t border-[#2a2a2a]" />
                <div className="px-6 py-5">
                  <h3 className="text-white text-sm mb-4">AC Details {index + 1}</h3>
                  <div className="grid grid-cols-2 gap-8 text-sm">
                    <div className="space-y-4">
                      <DetailRow label="Brand" value={ac?.brand || detailValue?.brand || 'N/A'} />
                      <DetailRow label="Model" value={ac?.model || 'N/A'} />
                      <DetailRow label="AC Type" value={ac?.acType || 'N/A'} />
                      <DetailRow label="Age of AC" value={ac?.age || 'N/A'} />
                    </div>
                    <div className="space-y-4">
                      <DetailRow label="AC Technology" value={ac?.technology || 'N/A'} />
                      <DetailRow label="Tonnage" value={ac?.tonnage || 'N/A'} />
                      <DetailRow label="Condition" value={ac?.condition || 'N/A'} />
                      <DetailRow label="Quoted Amount" value={ac?.inspectionDetails?.offeredAmount ? `Rs. ${ac.inspectionDetails.offeredAmount}` : 'N/A'} />
                    </div>
                  </div>

                  {ac?.photos?.length > 0 && (
                    <div className="grid grid-cols-2 gap-6 mt-6">
                      {ac.photos.map((img, i) => (
                        <img key={i} src={img} alt="AC" className="rounded-xl object-cover h-56 w-full" />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}

          {quotes.length > 0 && (
            <>
              <div className="border-t border-[#2a2a2a]" />
              <QuotationsCard quotes={quotes} />
            </>
          )}
        </div>

        <div className="mt-4 flex justify-between">
          <Button
            variant="outlined"
            sx={{ color: '#D32F2F', borderColor: '#D32F2F', padding: '10px 20px', borderRadius: '14px' }}
            startIcon={<Cancel />}
            disabled={!detailValue?.enquiryId || isCancelled}
            onClick={() => setCancelDialog(true)}
          >
            Cancel Request
          </Button>
          <div className="flex gap-3">
            <Button
              sx={{ color: '#fff', backgroundColor: '#E31E25', '&:hover': { backgroundColor: '#E31E25' }, padding: '10px 20px', borderRadius: '14px' }}
              startIcon={<Add />}
              disabled={!detailValue?._id}
              onClick={() => setQuoteModal(true)}
            >
              Add Quotation
            </Button>
            <Button
              sx={{ color: '#fff', backgroundColor: '#E31E25', '&:hover': { backgroundColor: '#E31E25' }, padding: '10px 20px', borderRadius: '14px' }}
              startIcon={<Engineering />}
              onClick={() => setAssignModal(true)}
            >
              {detailValue?.technician_info ? 'Reassign Inspector' : 'Assign Inspector'}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={cancelDialog} onClose={() => !cancelling && setCancelDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Sell Old AC Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Are you sure you want to cancel this request?</Typography>
          <TextField
            label="Cancellation reason"
            placeholder="Let the team know why this request is being cancelled"
            fullWidth
            multiline
            minRows={2}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)} disabled={cancelling}>Keep Request</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling...' : 'Cancel Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <AssignTechnicianModal
        isOpen={assignModal}
        onClose={() => setAssignModal(false)}
        bookingId={enquiryId}
        onReload={fetchDetail}
        isEnquiry={true}
      />

      <AddQuotation
        open={quoteModal}
        onClose={() => setQuoteModal(false)}
        id={detailValue?._id || ''}
        source="OLD_AC"
        enquiryId={detailValue?._id}
        onSaved={() => {
          fetchDetail();
          if (detailValue?._id)
            GETQuotesForEnquiry(detailValue._id).then(setQuotes);
        }}
      />
    </Box>
  );
}

const DetailRow = ({ label, value, green = false }: { label: string; value: string; green?: boolean }) => (
  <div className="flex items-center justify-between">
    <p className="text-gray-400 w-full">{label}</p>
    <p className={`${green ? 'text-green-500 underline' : 'text-white'} w-full`}>{value}</p>
  </div>
);

export default SellRequestDetail;
